import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { getUserTalentsByKeyword } from '../../services/UserTalentService';
import UserTalentElement from './UserTalentElement';
import { settingsPaginationUserTalent } from '../../common/lang';
import useAuth from '../../common/hooks/useAuth';

export default function UserTalentContainer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  // Only query the API while the dialog is open (it used to run on every page load).
  useEffect(() => {
    if (!open) return;
    const id = ++requestId.current;
    setLoading(true);
    getUserTalentsByKeyword(query, page - 1, settingsPaginationUserTalent.size, settingsPaginationUserTalent.sort)
      .then((res) => {
        if (id !== requestId.current) return;
        setList(res.data?.content || []);
        setTotalPages(Math.max(1, res.data?.totalPages || 1));
      })
      .catch(() => { if (id === requestId.current) setList([]); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
  }, [open, query, page]);

  const submit = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(draft.trim());
  };

  return (
    <>
      <Tooltip title={t('talent.userTalentTooltip')}>
        <IconButton onClick={() => setOpen(true)} aria-label={t('talent.userTalentTooltip')}>
          <PeopleAltOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} fullScreen={fullScreen} maxWidth="md" fullWidth PaperProps={{ sx: fullScreen ? { borderRadius: 0, m: 0, width: '100%' } : { height: 'min(720px, 90vh)' } }}>
        <DialogTitle sx={{ pb: 1, pt: fullScreen ? 'calc(16px + var(--safe-top))' : 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('talent.title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('talent.subtitle')}</Typography>
            </Box>
            <IconButton onClick={() => setOpen(false)} aria-label={t('common.close')}><CloseRoundedIcon /></IconButton>
          </Stack>
          <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1, mt: 2 }} role="search">
            <TextField
              fullWidth
              size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('talent.searchPlaceholder')}
              inputProps={{ enterKeyHint: 'search', 'aria-label': t('talent.searchPlaceholder') }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { minHeight: 44 } }}
            />
            <Button type="submit" variant="contained" sx={{ flexShrink: 0 }}>{t('talent.searchButton')}</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: '#F8FAFC' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : list.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <PeopleAltOutlinedIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
              <Typography color="text.secondary" sx={{ mt: 1 }}>{t('talent.noTalentUsers')}</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {list.map((talent) => (
                <UserTalentElement key={talent.id} userTalent={talent} userLogged={user} onContact={() => setOpen(false)} />
              ))}
            </Stack>
          )}
          {!loading && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5, pb: 'var(--safe-bottom)' }}>
              <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" siblingCount={0} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
